import React, {useCallback} from 'react';

import {GwgEnrollmentCTA} from '@udemy/career-academies';
import {Tracker} from '@udemy/event-tracking';
import {useI18n} from '@udemy/i18n';
import {PlanPeriodStyle, GwgSubscriptionCTA} from '@udemy/subscription-browse';
import {
    useActiveSubscriptions,
    useConsumerSubscriptionGwGPlan,
    useConsumerSubscriptionGwGPlusPersonalPlan,
} from '@udemy/subscription-graphql';

import {CourseTakingBrandPathEnrolled} from 'src/features/google-ai-path-page/events';
import {isSubscriberToAnyGwgPlan} from 'src/features/google-ai-plan-page/utils/is-gwg-subscriber';

export interface GrowWithGoogleCTAProps {
    acceleratorId: number;
    /** Accelerator title for enroll event */
    acceleratorTitle?: string;
    /** Course ID for tracking */
    courseId?: number;
    /** Course tracking ID */
    courseTrackingId?: string;
    /** UI region identifier for analytics */
    uiRegion: string;
    /** CTA button style */
    offerCtaStyle?: 'primary' | 'secondary';
    /** Button size */
    size?: 'small' | 'medium' | 'large';
    /** Additional CSS class name */
    className?: string;
    /** Plan period style (single or toggle) */
    planPeriodStyle?: PlanPeriodStyle;
    /** Whether to include trial days on CTA */
    includeTrialDaysOnCta?: boolean;
    /** Whether to show subtitle */
    showSubtitle?: boolean;
    /** CSS class for subtitle */
    subtitleClassName?: string;
}

/**
 * GrowWithGoogleCTA Component
 *
 * A reusable wrapper component for the GwgSubscriptionCTA that handles
 * subscription plan fetching and conditional rendering.
 *
 * Only renders when both gwgPlan and combinedGwgPlan are available.
 *
 * @example
 * ```tsx
 * <GrowWithGoogleCTA
 *   courseId={123}
 *   courseTrackingId="abc-123"
 *   uiRegion="hero-section"
 *   offerCtaStyle="primary"
 *   size="large"
 * />
 * ```
 */
export const GrowWithGoogleCTA: React.FC<GrowWithGoogleCTAProps> = ({
    acceleratorId,
    acceleratorTitle,
    courseId,
    courseTrackingId,
    uiRegion,
    offerCtaStyle = 'primary',
    size = 'large',
    className,
    planPeriodStyle = PlanPeriodStyle.SINGLE,
    includeTrialDaysOnCta = false,
    showSubtitle = true,
    subtitleClassName,
}) => {
    const {gettext} = useI18n();
    const currentlySubscribedPlan = useActiveSubscriptions()[0];
    const gwgPlan = useConsumerSubscriptionGwGPlan();
    const combinedGwgPlan = useConsumerSubscriptionGwGPlusPersonalPlan();

    const handleEnrollmentSuccess = useCallback(() => {
        Tracker.publishEvent(
            new CourseTakingBrandPathEnrolled({
                pathName: acceleratorTitle ?? '',
                uiRegion,
            }),
        );
    }, [acceleratorTitle, uiRegion]);

    if (isSubscriberToAnyGwgPlan(currentlySubscribedPlan)) {
        return (
            <GwgEnrollmentCTA
                acceleratorId={acceleratorId}
                udStyle={'primary'}
                onEnrollmentSuccess={handleEnrollmentSuccess}
                unEnrolledSubtitle={gettext(
                    'You will be enrolled in all courses included in this path.',
                )}
            />
        );
    }

    return (
        <GwgSubscriptionCTA
            plan={gwgPlan}
            currentlySubscribedPlan={currentlySubscribedPlan}
            combinedGwgPlan={combinedGwgPlan}
            courseId={courseId}
            courseTrackingId={courseTrackingId}
            uiRegion={uiRegion}
            offerCtaStyle={offerCtaStyle}
            size={size}
            className={className}
            planPeriodStyle={planPeriodStyle}
            includeTrialDaysOnCta={includeTrialDaysOnCta}
            showSubtitle={showSubtitle}
            subtitleClassName={subtitleClassName}
        />
    );
};
